<template>
  <div class="colors">
    <div
      v-if="swatchVisible"
      class="color-swatch py-3 d-flex flex-wrap mx-auto"
    >
      <div
        v-if="canAddNewColors"
        class="theme-color theme-color--new d-flex flex-column align-items-center justify-content-around"
        data-identity="add-new-theme-color"
        @click.stop.prevent="addNewColorShowPicker"
      >
        <i class="material-icons">add</i>
        <small>{{ $t('elements.properties.color.add') }}</small>
      </div>

      <div
        v-if="usage !== 'SiteSettings'"
        class="theme-color theme-color--new d-flex flex-column align-items-center justify-content-around"
        @click.stop.prevent="applyColorsAll({ colorId: null, toBg: toBg })"
      >
        <i class="material-icons">format_color_reset</i>
        <small>{{ $t('elements.properties.color.clear') }}</small>
      </div>

      <div
        v-for="(color, key) in colors"
        :key="key"
        class="theme-color theme-color--editable"
        :class="isActiveColor(key)"
        :style="colorToStyle(color)"
        @click.stop="applyColorsAll({ colorId: key, toBg: toBg })"
      >
        <div
          v-if="canEditColors"
          class="theme-color__edit__btn"
          @click.stop="enableColorPicker(color, key)"
        >
          {{ $t('editor.right_nav.theme.color_picker.edit.btn') }}
        </div>
      </div>
    </div>

    <div v-else-if="colorPickerVisible" class="color-picker">
      <div class="row no-gutters">
        <div class="col-auto">
          <color-picker
            ref="picker"
            :current-color="currentColor.color"
            :valpha="currentColor.valpha"
            @change="updateCurrentColor"
          />
        </div>
      </div>

      <div v-if="usage === ''" class="divider" />

      <div v-if="usage === ''" class="row">
        <div class="col d-flex">
          <cvt-button
            class="w-50 mb-2 mr-1"
            :color="defaultColorsCompBtnConfig.props.save.color"
            :special-style="defaultColorsCompBtnConfig.props.save.specialStyle"
            :outlined="defaultColorsCompBtnConfig.props.save.outlined"
            :text="$t('editor.right_nav.theme.color_picker.save.btn')"
            @click.prevent.stop="disableColorPicker"
          />
          <cvt-button
            class="w-50 mb-2 mr-1"
            mode="dark"
            color="light"
            :outlined="true"
            btn-text-color="#000"
            :text="
              $t(
                'editor.left_nav.page_manager.layout_dialog.template.footer.cancel.btn',
              )
            "
            @click.prevent.stop="resetToCurrentColor"
          />
          <cvt-button
            v-if="canAddNewColors"
            class="px-3 mb-2"
            :color="defaultColorsCompBtnConfig.props.delete.color"
            :special-style="
              defaultColorsCompBtnConfig.props.delete.specialStyle
            "
            :outlined="defaultColorsCompBtnConfig.props.delete.outlined"
            icon="trash"
            @click.prevent.stop="toggleRemoveColorDialog"
          />
        </div>
      </div>

      <div v-if="usage === 'SiteSettings'" class="row">
        <div class="col d-flex mt-1">
          <cvt-button
            class="site-settings-btns mr-1"
            :color="defaultSiteSettingsColorsCompBtnConfig.props.save.color"
            :special-style="
              defaultSiteSettingsColorsCompBtnConfig.props.save.specialStyle
            "
            :outlined="
              defaultSiteSettingsColorsCompBtnConfig.props.save.outlined
            "
            :text="$t('editor.right_nav.theme.color_picker.save.btn')"
            @click.prevent.stop="disableColorPicker"
          >
          </cvt-button>
          <cvt-button
            class="px-3 mr-1"
            mode="dark"
            color="light"
            :outlined="true"
            btn-text-color="#000"
            :text="
              $t(
                'editor.left_nav.page_manager.layout_dialog.template.footer.cancel.btn',
              )
            "
            @click.prevent.stop="resetToCurrentColor"
          >
          </cvt-button>
          <cvt-button
            class="px-3"
            :color="defaultSiteSettingsColorsCompBtnConfig.props.delete.color"
            :special-style="
              defaultSiteSettingsColorsCompBtnConfig.props.delete.specialStyle
            "
            :outlined="
              defaultSiteSettingsColorsCompBtnConfig.props.delete.outlined
            "
            icon="trash"
            @click.prevent.stop="toggleRemoveColorDialog"
          >
          </cvt-button>
        </div>
      </div>
    </div>

    <div v-if="alpha && swatchVisible" class="row">
      <div class="col-auto d-flex align-items-center">
        <h6 :class="textColorClass" class="m-0">
          {{ $t('elements.properties.color.transparency') }}
        </h6>
      </div>
      <div class="col d-flex align-items-center">
        <cvt-slider
          id="slider-default"
          :tooltips-enabled="true"
          :step-marks-enabled="true"
          class="slider"
          :min="0"
          :max="1"
          :value="transparencyVal"
          :step="0.1"
          @change="onChange"
        />
      </div>
    </div>

    <cvt-dialog
      :show="removeColorVisisble"
      :height-auto="true"
      :border-radius="'8px'"
      size="sm"
      bg-color="#fff"
      :show-close="false"
    >
      <template #title>
        <h3 class="del-color-warn-title">Warning</h3>
      </template>

      <template #default>
        <h5 class="del-color-warn-subtitle">
          Are you sure you want to remove this color?
        </h5>
      </template>

      <template #modalFooter>
        <div class="d-flex">
          <cvt-button
            class="mr-2"
            :text="$t('marketplace.dialog.archive.cta.cancel')"
            v-bind="cancelBtnProps"
            @click.stop="toggleRemoveColorDialog"
          />
          <cvt-button
            :text="$t('marketplace.dialog.archive.cta.submit')"
            v-bind="submitBtnProps"
            class="mr-2"
            @click.stop="deleteColorFromPalette"
          />
        </div>
      </template>
    </cvt-dialog>
  </div>
</template>
<script>
import color from 'color'
import * as _ from 'lodash'
import { mapActions, mapGetters, mapState } from 'vuex'
import { FRESHWORKS } from '../../../../../../storybook/components/constants'
import * as actions from '../../../store/modules/actions'
import * as getters from '../../../store/modules/getters'
import ColorPicker from '../../spectrum/ColorPicker.vue'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Colors',
  components: {
    ColorPicker,
  },
  props: {
    alpha: Boolean,
    mode: {
      type: String,
      default: 'LIGHT',
    },
    toBg: {
      type: Boolean,
      default: false,
    },
    usage: {
      type: String,
      default: '',
    },
    customOptions: {
      type: Object,
      default() {
        return {
          bindSelected: true,
          transparency: 1,
          activeColor: '',
        }
      },
    },
  },
  data() {
    return {
      colorPickerVisible: false,
      currentColor: color('white'),
      prevColor: color('white'),
      currentKey: null,
      removeColorVisisble: false,
      alphaChannel: 1,
      exclude: {
        GridBoxContainer: true,
        Icon: true,
      },
    }
  },

  computed: {
    ...mapState('globalTheme', {
      orgName: ({ globalTheme }) =>
        globalTheme.OrgName != null ? globalTheme.OrgName : 'standard',
      colorsCompBtnConfig: ({ globalButtonConfig }) =>
        globalButtonConfig.locations.colorsComponent,
      siteSettingsColorsCompBtnConfig: ({ globalButtonConfig }) =>
        globalButtonConfig.locations.siteSettingsColorComponent,
      deleteColorCancelTheme: ({ globalTheme }) =>
        globalTheme.deleteColorCancelTheme != null
          ? globalTheme.deleteColorCancelTheme
          : 'warning',
      defaultColorsCompBtnConfig: ({ globalButtonConfig }) =>
        globalButtonConfig.locations.colorsComponent.locations.default,
      defaultSiteSettingsColorsCompBtnConfig: ({ globalButtonConfig }) =>
        globalButtonConfig.locations.siteSettingsColorComponent.locations
          .default,
    }),

    ...mapGetters({
      colors: getters.THEME_GET_COLORS,
      defaultColor: getters.THEME_DEFAULT_COLOR,
      selected: getters.SELECTOR_SELECTED,
      config: getters.AUTH_GET_USER_CONFIG,
      isGridBuilder: getters.IS_GRID_BUILDER,
      canAddNewColors: getters.THEME_CAN_ADD_NEW_COLORS,
      canEditColors: getters.THEME_CAN_EDIT_COLORS,
    }),

    activeColor() {
      return this.selected
        ? this.isGridBuilder &&
          !this.exclude[this.selected.name] &&
          this.selected.parent().name === 'GridItemWrapper'
          ? this.selected.parent().themeColorId
          : this.selected.themeColorId
        : null
    },

    activeBgColor() {
      return this.selected
        ? this.isGridBuilder &&
          this.selected.parent().name === 'GridItemWrapper'
          ? this.selected.parent().themeColorId
          : null
        : null
    },

    swatchVisible() {
      return this.colorPickerVisible === false
    },

    saveBtnMsg() {
      return this.currentKey ? 'Save' : 'Add'
    },

    textColorClass() {
      return { 'text-white': this.mode === 'DARK' }
    },
    transparencyVal() {
      if (this.customOptions.bindSelected) {
        return this.alphaChannel
      } else {
        return this.customOptions.transparency
      }
    },

    isFreshworksOrg() {
      return this.orgName === FRESHWORKS
    },

    cancelBtnProps() {
      if (this.isFreshworksOrg) {
        return { color: 'light' }
      } else {
        return { color: this.deleteColorCancelTheme }
      }
    },
    submitBtnProps() {
      if (this.isFreshworksOrg) {
        return {
          color: '',
          specialStyle: 'dark',
        }
      } else {
        return {
          color: 'light',
        }
      }
    },
  },

  created() {
    this.alphaChannel = this.selected?.themeColorAlpha || 1
  },
  methods: {
    ...mapActions({
      addNewThemeColor: actions.THEME_ADD_NEW_COLOR,
      applyColorToSelected: actions.SELECTOR_UPDATE_THEME_COLOR,
      updateThemeColor: actions.THEME_UPDATE_COLOR,
      updateProp: actions.SELECTOR_UPDATE_PROP,
      removeColor: actions.THEME_REMOVE_COLOR,
    }),

    isActiveColor(color) {
      //Component is bind to selected
      if (this.customOptions.bindSelected) {
        return {
          'theme-color--active': !this.toBg
            ? this.activeColor === color
            : this.activeBgColor === color,
        }
      }
      //Active color is passed from parent
      else if (
        this.customOptions.activeColor &&
        this.customOptions.activeColor === color
      ) {
        return { 'theme-color--active': true }
      }
    },

    addNewColorShowPicker() {
      this.currentColor = color('white')
      this.addNewThemeColor(this.currentColor).then((colorId) => {
        this.enableColorPicker(this.currentColor, colorId)
        return this.applyColorsAll({ colorId: colorId, toBg: this.toBg })
      })
    },

    enableColorPicker(color, key) {
      this.currentKey = key
      this.currentColor = color
      this.prevColor = color
      this.colorPickerVisible = true

      // usage prop will always be equal to empty-string except
      // when this component is being rendered in "SiteSettings" component
      if (this.usage === '') {
        this.$nextTick((_) => {
          this.$refs.picker.setColor(this.currentColor)
        })
      }
    },

    disableColorPicker() {
      this.colorPickerVisible = false
      this.removeColorVisisble = false
    },

    resetToCurrentColor() {
      this.currentColor = this.prevColor

      this.updateThemeColor({
        color: this.currentColor,
        key: this.currentKey,
      })
      return this.disableColorPicker()
    },

    updateCurrentColor: _.debounce(function (color) {
      this.currentColor = color

      return this.updateThemeColor({
        color: this.currentColor,
        key: this.currentKey,
      })
    }, 300),

    colorToStyle(color) {
      return {
        backgroundColor: color.hsl().toString(),
      }
    },

    setCurrentColor(color) {
      this.currentColor = color
    },

    onChange(value) {
      if (this.customOptions.bindSelected) {
        this.alphaChannel = value
        this.updateProp({
          key: 'themeColorAlpha',
          value,
        })
      } else {
        this.$emit('apply-transparency', value)
      }
    },

    applyColorsAll(options) {
      if (this.usage === 'SiteSettings') {
        return
      }

      if (this.customOptions.bindSelected) {
        return this.applyColorToSelected(options)
      } else {
        this.$emit('apply-color', options)
      }
    },

    toggleRemoveColorDialog() {
      this.removeColorVisisble = !this.removeColorVisisble
    },

    deleteColorFromPalette() {
      try {
        this.removeColor(this.currentKey)
        this.disableColorPicker()
      } catch (e) {
        console.log(e)
        this.$message({
          type: 'info',
          message: this.$t(
            'editor.right_nav.theme.color_picker.delete.failed.message',
          ),
        })
      }
    },
  },
}
</script>
<style lang="scss">
@import '@/scss/utils';

.theme-color__edit__btn {
  transition: all 250ms ease;
  display: none;
  top: -10px;
  right: -10px;
  position: absolute;
  box-shadow: 0 0 0 2px rgba(63, 70, 82, 0.15);
  background: $white;
  border-radius: 3px;
  border: 1px solid $dark;
  color: $dark;
  z-index: 3;
  padding: 0 5px;
  // text-transform: uppercase;
  text-align: center;

  &:hover {
    background: $info;
    border-color: transparent;
    color: $white;
  }
}

.theme-color {
  transition: all 100ms ease;
  transform: scale(1);
  width: 3rem;
  height: 3rem;

  box-shadow: inset 2px 1px 0 rgba(0, 0, 0, 0.05),
    inset -2px -1px 0 rgba(0, 0, 0, 0.05);

  cursor: pointer;
  user-select: none;

  display: inline-block;
  position: relative;

  margin: 5px;
  border-radius: 5px;

  &--active {
    outline: 4px solid $light-blue;
    z-index: 2;
  }

  &--editable {
    .theme-color__edit__btn {
      display: flex;
      opacity: 0;
      padding: 0.2rem 0.5rem;
      &:hover {
        background: $bright-blue;
      }
    }

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

      .theme-color__edit__btn {
        opacity: 1;
      }

      z-index: 3;
    }
  }

  &:active {
    transform: scale(0.95);
  }
}

.theme-color--new {
  // outline: 1px solid $dark-gray;
  font-size: 25px;
  box-shadow: none;
  small {
    font-size: 8px;
    text-transform: uppercase;
    line-height: 1;
    text-align: center;
  }

  &:hover {
    background: $bright-blue !important;
    color: $white;
  }
}

.color-picker {
  width: 100%;
}

.color-picker-controls {
  width: 100%;
  margin-top: 10px;
  .el-button {
    width: 50%;
  }
}

.vue-color__chrome {
  box-shadow: none !important;
  margin: 0 auto;
  display: flex;
  -webkit-flex-direction: column;
  -ms-flex-direction: column;
  flex-direction: column;
  align-items: center;
  background: transparent !important;
}

.vue-color__chrome__chrome-body {
  background: transparent !important;
}

.site-settings-btns {
  padding-left: 2.4rem !important;
  padding-right: 2.4rem !important;
}

.colors {
  .del-color-warn-title {
    font-style: normal;
    font-weight: 600;
    font-size: 18px;
    line-height: 24px;
    color: #12344d;
  }
  .del-color-warn-subtitle {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #12344d;
  }
}
</style>
